import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  useTheme,
  Typography,
} from '@mui/material';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const menuItems = [
    { label: 'Find', path: '/', icon: <ViewStreamIcon /> },
    { label: 'People', path: '/contacts', icon: <GroupIcon /> },
    { label: 'Preferences', path: '/account', icon: <SettingsIcon /> },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <Box
      sx={{
        width: 72,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
      }}
    >
      <Box
        onClick={() => navigate('/')}
        aria-label="InTouch"
        sx={{
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          cursor: 'pointer',
          position: 'relative',
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:hover .dock-logo': {
            transform: 'scale(1.28) translateX(3px)',
            filter: `drop-shadow(0 8px 18px ${theme.palette.primary.main}55)`,
          },
          '&:hover .dock-label': {
            opacity: 1,
            transform: 'translateX(8px) scale(1.04)',
            pointerEvents: 'auto',
          },
        }}
      >
        <Box
          component="img"
          className="dock-logo"
          src="/logo-intouch.svg"
          alt="InTouch"
          sx={{
            height: 34,
            width: 34,
            objectFit: 'contain',
            transition: 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1), filter 220ms ease',
          }}
        />
        <Box
          className="dock-label"
          sx={{
            position: 'absolute',
            left: 64,
            top: '50%',
            transform: 'translateX(-10px) translateY(-50%) scale(0.92)',
            opacity: 0,
            px: 1.75,
            py: 0.95,
            borderRadius: '10px',
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.primary.main}55`,
            boxShadow: `0 14px 34px ${theme.palette.common.black}55, 0 0 0 1px ${theme.palette.primary.main}22`,
            fontSize: '0.9rem',
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            transition: 'opacity 160ms ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
            zIndex: 2,
          }}
        >
          InTouch
        </Box>
      </Box>

      <List sx={{ flex: 1, pt: 1, px: 1 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem
              key={item.path}
              onClick={() => navigate(item.path)}
              aria-label={item.label}
              sx={{
                cursor: 'pointer',
                width: 56,
                height: 48,
                p: 0,
                mb: 0.5,
                borderRadius: '8px',
                justifyContent: 'center',
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: active ? theme.palette.action.selected : 'transparent',
                position: 'relative',
                overflow: 'visible',
                transition: 'background-color 0.18s ease, color 0.18s ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                  transform: 'translateX(6px) scale(1.08)',
                  boxShadow: `0 10px 28px ${theme.palette.primary.main}22`,
                },
                '&:hover .dock-icon': {
                  transform: 'scale(1.36)',
                  filter: `drop-shadow(0 8px 16px ${theme.palette.primary.main}66)`,
                },
                '&:hover .dock-label': {
                  opacity: 1,
                  transform: 'translateX(12px) translateY(-50%) scale(1.04)',
                  pointerEvents: 'auto',
                },
              }}
            >
              <ListItemIcon
                className="dock-icon"
                sx={{
                  color: 'inherit',
                  minWidth: 0,
                  justifyContent: 'center',
                  transition: 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1), filter 220ms ease',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <Box
                className="dock-label"
                sx={{
                  position: 'absolute',
                  left: 64,
                  top: '50%',
                  transform: 'translateX(-12px) translateY(-50%) scale(0.9)',
                  opacity: 0,
                  px: 1.75,
                  py: 0.95,
                  borderRadius: '10px',
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.primary.main}55`,
                  boxShadow: `0 14px 34px ${theme.palette.common.black}55, 0 0 0 1px ${theme.palette.primary.main}22`,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  transition: 'opacity 160ms ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
                  zIndex: 2,
                }}
              >
                {item.label}
              </Box>
            </ListItem>
          );
        })}
      </List>

      <Box
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.65rem',
          }}
        >
          v1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;
